import { createContext, useCallback, useContext, useEffect, useState } from 'react';

import api from '../api/api';
import {VisitGuides} from '../api/models/guides';
import EquipModels from './equip-models.json';
import { Variable } from '../api/models/variables';
import { ProfileContext } from './profile';

export type VisitGuidesContextType = {
  getGuide(guideId: string, payload?: any): EntityBase[] | false;
  variables: Record<string, Variable>;
};

export const VisitGuidesContext = createContext<VisitGuidesContextType>(null);

const preloadVariables = ['equipQR_validate_regexp', 'MENU_COUNTERS_ACCESS', 'SNEG_APP_DOWNLOAD_LINK', 'SNEG_APP_REQUIRED_APP_VER'];

export const useVisitGuidesContext = (): VisitGuidesContextType => {
  const [guides, setGuides] = useState<VisitGuides>();
  const { profile } = useContext(ProfileContext);
  const [variables, setVariables] = useState<Record<string, Variable>>({});

  useEffect(() => {
    if (!profile) {return;}
    (async () => {
      const { data } = await api.offline.getGuides();
      setGuides({ ...data.data, equipment_model_mock: EquipModels });

      const { data: response } = await api.service.getVars({
        keys: preloadVariables.join(','),
      });

      const vars = response.data
        .filter((item) => !!item)
        .reduce((acc, item) => ({ ...acc, [item.key]: item }), {});

      setVariables(vars);
    })();
  }, [profile]);

  // TODO: debug this
  const getGuide = useCallback(
    (guideId: string, payload?: Record<string, string>) => {
      if (!guides || !guides[guideId]) {return false;}

      if (payload) {
        return guides[guideId].filter((item) => {
          if (payload.stand_brand_id && payload.equip_model_id) {
            return (
              (item as { brand: EntityBase }).brand.id.toString() ===
                payload.stand_brand_id &&
              (
                item as { equipment_model_stand: EntityBase }
              ).equipment_model_stand.id.toString() === payload.equip_model_id
            );
          }
          if (payload.equip_model_id) {
            return (
              (
                item as { equipment_model_stand: EntityBase }
              ).equipment_model_stand.id.toString() === payload.equip_model_id
            );
          }
          return false;
        });
      }

      return guides[guideId];
    },
    [guides]
  );

  return {
    getGuide,
    variables,
  };
};

export const VisitGuidesContextProvider = ({ children }) => {
  const value = useVisitGuidesContext();

  return (
    <VisitGuidesContext.Provider value={value}>
      {children}
    </VisitGuidesContext.Provider>
  );
};
